import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios'; 

// IMPORTANT: Ensure this path points to your actual config file
import { API_BASE_URL } from '../../config'; 

import JobCard from './JobCard';
import styles from './AvailableJobs.module.css';

// Import slick css files
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

// Custom Arrow Components
const NextArrow = ({ onClick }) => {
  return (
    <button className={`${styles.navBtn} ${styles.navBtnNext}`} onClick={onClick} aria-label="Next">
      <ChevronRight size={24} />
    </button>
  );
};

const PrevArrow = ({ onClick }) => {
  return (
    <button className={`${styles.navBtn} ${styles.navBtnPrev}`} onClick={onClick} aria-label="Previous">
      <ChevronLeft size={24} />
    </button>
  );
};

const AvailableJobs = () => {
  const navigate = useNavigate();
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch and format data
  const fetchJobs = async () => {
    try {
      // 1. Fetch from your API
      const response = await axios.get(`${API_BASE_URL}/api/Jobs/GetLatestJobs`);
      
      const rawData = response.data || [];

      // 2. Transform API data to match JobCard props
      // We slice(0, 10) to keep the carousel performant
      const formattedData = rawData.slice(0, 12).map((job) => ({
        id: job.job_id, // Unique ID
        title: job.name, // The trade/title
        description: job.description,
        // Construct a readable address from available fields
        address: [job.suburb, job.postcode, job.state]
          .filter(Boolean) // Removes null/undefined/empty strings
          .join(', '), 
        type: job.name, // Used for icon selection
      }));

      setJobsData(formattedData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Unable to load live jobs.");
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchJobs();

    // 3. Polling: Re-fetch every 3 minutes (180,000 ms) to keep data fresh
    const intervalId = setInterval(() => {
      fetchJobs();
    }, 3 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleCardClick = () => {
    navigate('/business-landing'); 
  };


  const handleAccessLeadsClick = () => {
    // 1. Try to find the pricing section on the current page
    const pricingSection = document.getElementById('pricing');

    if (pricingSection) {
      // 2. If it exists (we are on the Business Landing Page), scroll to it
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // 3. Fallback: If this component is used on a page WITHOUT pricing (e.g. Home Page),
      // navigate to the business page and scroll to pricing.
      // Note: This requires hash handling on the destination page, 
      // but simpler is just sending them to the register page directly:
      navigate('/register/business');
    }
  };



  const settings = {
    dots: true,
    infinite: true, // Only infinite if we have enough items
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };

  // If fetching fails or array is empty, we can optionally hide the section 
  // or show a specific message. For now, we render the empty wrapper.
  if (!loading && jobsData.length === 0) {
    return null; // Or return <p>No jobs available right now.</p>
  }

  return (
    <section className={styles.jobsSection}>
      <div className={styles.container}>
        
        {/* Header Section */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Available jobs</h1>
            <p className={styles.subtitle}>
              Someone just posted these jobs in your area. Don't miss out!
            </p>
          </div>
          <button 
            className={styles.ctaBtn} 
            onClick={handleAccessLeadsClick} 
          >
            Access Job Leads today
          </button>
        </header>

        {/* Carousel Section */}
        <div className={styles.sliderWrapper}>
          {loading ? (
             <div style={{color: 'white', textAlign: 'center', padding: '40px'}}>
               Loading live opportunities...
             </div>
          ) : (
            <Slider {...settings}>
              {jobsData.map((job) => (
                <JobCard
                  key={job.id}
                  title={job.title}
                  description={job.description}
                  address={job.address}
                  type={job.type}
                  onClick={handleCardClick}
                />
              ))}
            </Slider>
          )}
        </div>

      </div>
    </section>
  );
};

export default AvailableJobs;